import { useState, useMemo, useCallback } from 'react';

/**
 * Reusable hook for sortable table columns.
 *
 * Cycle: null → asc → desc → null (reset to default)
 *
 * @param {Array} data - The array of items to sort
 * @param {Object} columnAccessors - Map of columnKey → accessor function or dot-path string
 *   e.g. { date: 'job_date', customer: (row) => row.customer_name?.toLowerCase() }
 * @returns {{ sortedData, sortConfig, requestSort, getSortDirection, resetSort }}
 */
export function useSortableTable(data, columnAccessors = {}) {
  // sortConfig: { key: string, direction: 'asc' | 'desc' } | null
  const [sortConfig, setSortConfig] = useState(null);

  const requestSort = useCallback((key) => {
    setSortConfig(prev => {
      if (!prev || prev.key !== key) {
        // New column or no active sort → ascending
        return { key, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        // Same column, was asc → descending
        return { key, direction: 'desc' };
      }
      // Same column, was desc → reset (third click)
      return null;
    });
  }, []);

  const getSortDirection = useCallback((key) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction;
  }, [sortConfig]);

  const resetSort = useCallback(() => setSortConfig(null), []);

  const sortedData = useMemo(() => {
    if (!sortConfig || !data) return data;

    const accessor = columnAccessors[sortConfig.key];
    if (!accessor) return data;

    const getValue = typeof accessor === 'function'
      ? accessor
      : (row) => {
          // Support dot-path like 'customer.name'
          return accessor.split('.').reduce((obj, k) => obj?.[k], row);
        };

    return [...data].sort((a, b) => {
      let aVal = getValue(a);
      let bVal = getValue(b);

      // Handle nulls/undefined — push to bottom
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Numeric comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison (case-insensitive)
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, columnAccessors]);

  return { sortedData, sortConfig, requestSort, getSortDirection, resetSort };
}

/**
 * Server-side sort controller for paginated list views (useInfiniteList).
 *
 * Provides the same requestSort/getSortDirection API as useSortableTable so
 * <SortableTableHead> works unchanged, but instead of sorting in-memory it
 * exposes `sortParams` ({ sort_by, sort_dir }) to thread into the list's
 * `params` so the backend does the sorting across ALL pages.
 *
 * Cycle: null → asc → desc → null (reset to backend default)
 */
export function useServerSort() {
  const [sortConfig, setSortConfig] = useState(null);

  const requestSort = useCallback((key) => {
    setSortConfig(prev => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);

  const getSortDirection = useCallback((key) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction;
  }, [sortConfig]);

  const sortParams = useMemo(() => (
    sortConfig ? { sort_by: sortConfig.key, sort_dir: sortConfig.direction } : {}
  ), [sortConfig]);

  return { sortConfig, sortParams, requestSort, getSortDirection };
}
